import { motion } from 'framer-motion';
import { Shield, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../mock';

export const Hero = () => {
  const navigate = useNavigate();
  const logoUrl = 'https://customer-assets-eiarnc6j.emergentagent.net/job_ot-partners/artifacts/sglfjlo8_OT_Resilience_LOGO-removebg-preview.png';

  const titleVariants = {
    hidden: { opacity: 0 },
    visible: (i) => ({
      opacity: 1,
      transition: {
        delay: i * 0.3 + 0.5,
        duration: 0.8,
        ease: [0.25, 0.4, 0.25, 1]
      }
    })
  };

  const lineReveal = {
    hidden: { width: 0 },
    visible: {
      width: '100%',
      transition: {
        duration: 1.5,
        ease: [0.25, 0.4, 0.25, 1]
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />
      
      {/* Spotlight Effect */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.15 }}
        transition={{ duration: 2 }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-amber-500 rounded-full blur-[80px]"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
        {/* Logo with entrance animation */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }}
          className="mb-16 inline-block"
        >
          <img 
            src={logoUrl} 
            alt="OT Resilience" 
            className="h-64 md:h-72 w-auto mx-auto"
            style={{ 
              objectFit: 'cover', 
              objectPosition: 'center top',
              clipPath: 'inset(0 0 28% 0)'
            }}
          />
        </motion.div>

        {/* Kinetic Title with Line-by-Line Reveal */}
        <div className="space-y-4 mb-8">
          {mockData.hero.title.map((word, i) => (
            <div key={i} className="overflow-hidden">
              <motion.h1
                custom={i}
                initial="hidden"
                animate="visible"
                variants={titleVariants}
                className="text-4xl md:text-7xl lg:text-9x1 font-bold tracking-tight"
              >
                <span className="bg-gradient-to-r from-white via-amber-50 to-amber-400 bg-clip-text text-transparent">
                  {word}
                </span>
              </motion.h1>
            </div>
          ))}
        </div>

        {/* Animated Line */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={lineReveal}
          className="h-[2px] bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mb-8 overflow-hidden"
        />

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto font-light tracking-wide"
        >
          {mockData.hero.subtitle}
        </motion.p>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            navigate('/contact');
            window.scrollTo({ top: 0, behavior: 'instant' });
          }}
          className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-semibold rounded-full overflow-hidden shadow-2xl shadow-amber-500/30 transition-all duration-300 hover:shadow-amber-500/50"
        >
          <span className="relative z-10">{mockData.hero.cta}</span>
          <ArrowRight className="relative z-10 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-amber-300 to-amber-400"
            initial={{ x: '-100%' }}
            whileHover={{ x: 0 }}
            transition={{ duration: 0.3 }}
          />
        </motion.button>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.8 }}
          className="mt-24 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
        >
          {mockData.hero.stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2 + i * 0.15, duration: 0.6 }}
              className="group relative p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-amber-400/50 transition-all duration-300"
            >
              <div className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">{stat.number}</div>
              <div className="text-slate-400 text-sm tracking-wider uppercase">{stat.label}</div>
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/0 to-amber-400/0 group-hover:from-amber-400/5 group-hover:to-transparent rounded-2xl transition-all duration-300" />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-amber-400/50 rounded-full flex items-start justify-center p-2"
        >
          <motion.div 
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-1.5 h-1.5 bg-amber-400 rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
